import { useState } from "react";
import {
  FaUser, FaLock, FaEye, FaEyeSlash,
  FaPhone, FaEnvelope, FaStore, FaVideo,
  FaMapMarkerAlt, FaIdCard, FaCheckCircle,
} from "react-icons/fa";
import { Breadcrumb, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axios";
import { validateEmail, validatePhone } from "../../constants/validations";

const Register = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Pre-filled values passed from the chat flow

  const [reg, setReg] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    name:  "",
    phone: "",
    email: "",
    outlet: "",
    organizationName: "",
    totalCamera: "",
    location:         "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [loading, setLoading]           = useState(false);

  const handleChange = (field) => (e) =>
    setReg((prev) => ({ ...prev, [field]: e.target.value }));
 
  // Field label map for friendly error messages
  const fieldLabels = {
    username:         "Username",
    password:         "Password",
    confirmPassword:  "Confirm Password",
    name:             "Full Name",
    phone:            "Phone",
    email:            "Email",
    outlet:           "No. of Outlets",
    organizationName: "Organization Name",
    totalCamera:      "Total Cameras",
    location:         "Location",
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
 
    const required = [
      "username","password","confirmPassword","name","phone",
      "email","outlet","organizationName","totalCamera","location",
    ];
 
    // Collect all empty fields and show their names
    const emptyFields = required.filter((key) => !reg[key]?.toString().trim());
    if (emptyFields.length > 0) {
      const names = emptyFields.map((k) => fieldLabels[k]).join(", ");
      return message.warning(`Please fill: ${names}`);
    }
 
    const phoneError = validatePhone(reg.phone);
    if (phoneError) return message.error(phoneError);
 
    const emailError = validateEmail(reg.email);
    if (emailError) return message.error(emailError);
 
    if (reg.password !== reg.confirmPassword) return message.error("Passwords do not match");
    if (reg.password.length < 6) return message.warning("Password must be at least 6 characters");
 
    setLoading(true);
    try {
      await axios.post("/register-user", {
        username:         reg.username,
        password:         reg.password,
        name:             reg.name,
        phone:            reg.phone,
        email:            reg.email,
        outlet:           reg.outlet,
        organizationName: reg.organizationName,
        totalCamera:      reg.totalCamera,
        location:         reg.location,
      });
      message.success("Registration Successful! Please login.");
      navigate("/loginconfiguration");
    } catch (err) {
      message.error(err.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };
 
  const handleSendOtp = async () => {
    if (!reg.email?.trim()) return message.warning("Please enter email first");
    const emailError = validateEmail(reg.email);
    if (emailError) return message.error(emailError);
 
    setOtpLoading(true);
    try {
      await axios.post("/send-otp-register", { email: reg.email });
      message.success("OTP sent to your email");
      setOtpSent(true);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };
 
  const handleVerifyOtp = async () => {
    if (!otp.trim()) return message.warning("Please enter OTP");
    setOtpLoading(true);
    try {
      await axios.post("/verify-otp-register", { email: reg.email, otp });
      message.success("OTP verified successfully");
      setOtpVerified(true);
    } catch (err) {
      message.error(err.response?.data?.message || "Invalid OTP");
    } finally {
      setOtpLoading(false);
    }
  };
 
  const inputBase =
    "w-full h-11 pl-11 pr-4 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-400 focus:outline-none text-sm transition-colors";
   const inputDisabled =
    "w-full h-11 pl-11 pr-4 rounded-xl border-2 border-green-200 bg-green-50 text-sm text-slate-700 cursor-not-allowed";
  const fieldWrap = "space-y-1";
  const label     = "text-xs font-semibold text-slate-600 uppercase tracking-wide";
  const iconBase  = "absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm";
 
 
  // Required asterisk component
  const Req = () => <span className="text-red-500 ml-0.5">*</span>;
 
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 p-4">
 
      {/* Breadcrumb */}
      <div className="absolute top-6 left-6 z-30">
        <Breadcrumb>
          <Breadcrumb.Item
            onClick={() => navigate("/landing-page")}
            className="cursor-pointer text-slate-600 hover:text-blue-600 font-medium"
          >
            <ArrowLeftOutlined className="mr-1" /> Back
          </Breadcrumb.Item>
          <Breadcrumb.Item className="text-slate-400">Register</Breadcrumb.Item>
        </Breadcrumb>
      </div>
 
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200/50 p-8">
 
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-800 mb-1">Create Account</h1>
            <p className="text-slate-500 text-sm">Fill in the details to get started</p>
 
          </div>
 
          <form onSubmit={handleSubmit} className="space-y-3" autoComplete="off">
 
            {/* Full Name */}
            <div className={fieldWrap}>
              <label className={label}>
                Full Name <Req />
              </label>
              <div className="relative">
                <FaIdCard className={iconBase} />
                <input
                  type="text"
                  value={reg.name}
                  onChange={handleChange("name")}
                  placeholder="Enter your full name"
                  className={inputBase}
                />
              </div>
            </div>
 
            {/* Username */}
            <div className={fieldWrap}>
              <label className={label}>Username <Req /></label>
              <div className="relative">
                <FaUser className={iconBase} />
                <input
                  name="new-username"
                  autoComplete="new-username"
                  type="text"
                  value={reg.username}
                  onChange={handleChange("username")}
                  placeholder="Choose a username"
                  className={inputBase}
                />
              </div>
            </div>
 
            {/* Phone */}
            <div className={fieldWrap}>
              <label className={label}>
                Phone <Req />
              </label>
              <div className="relative">
                <FaPhone className={iconBase}/>
                <input
                  type="tel"
                  value={reg.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) handleChange("phone")({ target: { value } });
                  }}
                  placeholder="Enter phone number"
                  className={inputBase}
                />
              </div>
            </div>
 
            {/* Email */}
            <div className={fieldWrap}>
              <label className={label}>
                Email <Req />
                {otpVerified && (
                  <span className="inline-flex items-center gap-1 ml-2 text-green-600 normal-case font-semibold text-xs">
                    <FaCheckCircle className="text-green-500" /> Verified
                  </span>
                )}
              </label>
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  {/* Show green check icon inside field when verified */}
                  {otpVerified ? (
                    <FaCheckCircle
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-green-500 text-sm"
                    />
                  ) : (
                    <FaEnvelope className={iconBase} />
                  )}
                  <input
                    type="email"
                    value={reg.email}
                    onChange={handleChange("email")}
                    placeholder="Enter email"
                    className={otpVerified ? inputDisabled : inputBase}
                    disabled={otpSent || otpVerified}
                  />
                </div>
 
                {!otpSent && !otpVerified && (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpLoading}
                    className="px-4 rounded-xl bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 transition-colors"
                  >
                    {otpLoading ? "Sending..." : "Send OTP"}
                  </button>
                )}
              </div>
            </div>
 
            {/* OTP Input */}
            {otpSent && !otpVerified && (
              <div className={fieldWrap}>
                <label className={label}>Enter OTP <Req /></label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter OTP"
                    className={inputBase}
                  />
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={otpLoading}
                    className="px-4 rounded-xl bg-green-500 text-white text-sm font-semibold hover:bg-green-600 transition-colors"
                  >
                    {otpLoading ? "Verifying..." : "Verify"}
                  </button>
                </div>
              </div>
            )}
 
            {/* Outlet */}
            <div className={fieldWrap}>
              <label className={label}>
                No. of Outlets <Req />
              </label>
              <div className="relative">
                <FaStore className={iconBase} />
                <input
                  type="text"
                  value={reg.outlet}
                  onChange={handleChange("outlet")}
                  placeholder="Enter number of outlets"
                  className={inputBase}
                />
              </div>
            </div>
 
            {/* Organization Name */}
            <div className={fieldWrap}>
              <label className={label}>
                Organization Name <Req />
              </label>
              <div className="relative">
                <FaStore className={iconBase}/>
                <input
                  type="text"
                  value={reg.organizationName}
                  onChange={handleChange("organizationName")}
                  placeholder="Enter organization name"
                  className={inputBase}
                />
              </div>
            </div>
 
            {/* Total Cameras */}
            <div className={fieldWrap}>
              <label className={label}>
                Total Cameras <Req />
              </label>
              <div className="relative">
                <FaVideo className={iconBase} />
                <input
                  type="number"
                  value={reg.totalCamera}
                  onChange={handleChange("totalCamera")}
                  placeholder="Number of cameras"
                  className={inputBase}
                />
              </div>
            </div>
 
            {/* Location */}
            <div className={fieldWrap}>
              <label className={label}>Location <Req /></label>
              <div className="relative">
                <FaMapMarkerAlt className={iconBase} />
                <input
                  type="text"
                  value={reg.location}
                  onChange={handleChange("location")}
                  placeholder="Enter location"
                  className={inputBase}
                />
              </div>
            </div>
 
            {/* Password */}
            <div className={fieldWrap}>
              <label className={label}>Password <Req /></label>
              <div className="relative">
                <FaLock className={iconBase} />
                <input
                  name="new-password"
                  autoComplete="new-password"
                  type={showPassword ? "text" : "password"}
                  value={reg.password}
                  onChange={handleChange("password")}
                  placeholder="Create password"
                  className={`${inputBase} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
 
            {/* Confirm Password */}
            <div className={fieldWrap}>
              <label className={label}>Confirm Password <Req /></label>
              <div className="relative">
                <FaLock className={iconBase} />
                <input
                  type={showConfirm ? "text" : "password"}
                  value={reg.confirmPassword}
                  onChange={handleChange("confirmPassword")}
                  placeholder="Confirm password"
                  className={`${inputBase} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showConfirm ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
 
            <button
              type="submit"
              disabled={!otpVerified || loading}
              className={`w-full h-12 rounded-xl font-semibold shadow-lg transition mt-2
              ${otpVerified
                ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:shadow-xl"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"}
              `}
            >
              {loading ? "Registering..." : "Create Account"}
            </button>
 
          </form>
        </div>
      </div>
    </div>
  );
};
 
export default Register;